// ─── Figure & Table Numbering Engine ─────────────────────────────────────────
// Assigns deterministic Figure X.Y / Table X.Y numbers across all chapters.
// Scans chapter HTML for <div data-thesis-figure> tokens and the thesis_figures
// DB rows, reconciles, and returns authoritative registries.

import { safeDecode } from './exportCompositionEngine.js'

// ── Token attribute reader ────────────────────────────────────────────────────

function getAttr(attrsStr, name) {
  const m = attrsStr.match(new RegExp(`data-${name}="([^"]*)"`, 'i'))
  return m ? safeDecode(m[1]) : ''
}

// ── Scan a single chapter's HTML for figure tokens ───────────────────────────

function scanChapterTokens(chapterNum, html) {
  if (!html) return []
  const items = []
  const re = /<div\s+data-thesis-figure="true"([^>]*)><\/div>/gi
  let m
  while ((m = re.exec(html)) !== null) {
    const attrs = m[1]
    items.push({
      token:      getAttr(attrs, 'figure-id'),
      type:       getAttr(attrs, 'figure-type') || 'mermaid',
      title:      getAttr(attrs, 'figure-title'),
      caption:    getAttr(attrs, 'figure-caption'),
      code:       getAttr(attrs, 'figure-code'),
      chapterNum,
    })
  }
  return items
}

// ── Build registries ─────────────────────────────────────────────────────────

/**
 * Scan all chapters and build deterministic figure + table registries.
 *
 * @param {Array} chapters  - rows from chapters table, sorted by chapter_number
 * @param {Array} dbFigures - rows from thesis_figures table (supplementary metadata)
 * @returns {{ figures: Map, tables: Map }}
 *   Each Map is keyed by token → { figureNumber, tableNumber, title, caption, code, type, chapterNum }
 */
export function buildNumberingRegistries(chapters, dbFigures = []) {
  const figureMap = new Map() // token → entry
  const tableMap  = new Map()

  // Build lookup from DB for title/caption overrides
  const dbLookup = new Map(dbFigures.map(f => [f.token, f]))

  for (const ch of chapters) {
    const chNum  = ch.chapter_number || 0
    const tokens = scanChapterTokens(chNum, ch.content || '')

    let figSeq = 1
    let tblSeq = 1

    for (const tok of tokens) {
      const db    = dbLookup.get(tok.token) || {}
      const title   = tok.title   || db.title   || ''
      const caption = tok.caption || db.caption || ''
      const code    = tok.code    || db.code    || ''

      if (tok.type === 'table') {
        const number = `${chNum}.${tblSeq++}`
        tableMap.set(tok.token, {
          token:       tok.token,
          tableNumber: number,
          displayLabel: `Table ${number}`,
          title, caption, code,
          type:        'table',
          chapterNum:  chNum,
          chapterId:   ch.id,
        })
      } else {
        const number = `${chNum}.${figSeq++}`
        figureMap.set(tok.token, {
          token:        tok.token,
          figureNumber: number,
          displayLabel: `Figure ${number}`,
          title, caption, code,
          type:         tok.type || 'mermaid',
          chapterNum:   chNum,
          chapterId:    ch.id,
        })
      }
    }
  }

  return { figures: figureMap, tables: tableMap }
}

// ── Cross-reference updater ───────────────────────────────────────────────────

/**
 * Rewrite "Figure X.Y" and "Table X.Y" cross-references in HTML using the
 * authoritative registries. Also fixes wrong references that point to stale
 * chapter numbers (common when chapters are reordered).
 *
 * Strategy: replace all "Figure N.M" and "Table N.M" that appear in prose
 * (NOT inside a data-thesis-figure div) with the correct numbers from the
 * registry keyed by matching title text.
 *
 * Since cross-references are hard to resolve automatically without token IDs,
 * we at minimum flag them in validation — the actual rewrite would need the
 * LLM to have embedded token IDs in cross-references, which is a future phase.
 *
 * For now: return HTML unchanged (cross-reference rewriting is a Phase 2 task).
 */
export function rewriteCrossReferences(html, figures, tables) {
  // Future: implement token-ID-based cross-reference rewriting
  return html
}

// ── Duplicate number detector ─────────────────────────────────────────────────

export function findDuplicateNumbers(figures, tables) {
  const errors = []
  const figNums = new Map()
  const tblNums = new Map()

  for (const [, entry] of figures) {
    if (figNums.has(entry.figureNumber)) {
      errors.push({ type: 'duplicate_figure', number: entry.figureNumber, tokens: [figNums.get(entry.figureNumber), entry.token] })
    } else {
      figNums.set(entry.figureNumber, entry.token)
    }
  }

  for (const [, entry] of tables) {
    if (tblNums.has(entry.tableNumber)) {
      errors.push({ type: 'duplicate_table', number: entry.tableNumber, tokens: [tblNums.get(entry.tableNumber), entry.token] })
    } else {
      tblNums.set(entry.tableNumber, entry.token)
    }
  }

  return errors
}

// ── Stats helper ─────────────────────────────────────────────────────────────

export function registryStats(figures, tables) {
  return {
    figureCount: figures.size,
    tableCount:  tables.size,
    missingCaptions: [
      ...[...figures.values()].filter(f => !f.caption),
      ...[...tables.values()].filter(t => !t.caption),
    ],
  }
}
